import React, { type FC, useState } from 'react';
import InputMask from 'react-input-mask';
import { useSelector } from 'react-redux';

import { useFeatureFlags } from '@core/context/FeatureFlagsContext';
import useElementEvents from '@core/hooks/cohesion/useElementEvents';
import useFieldEvents from '@core/hooks/cohesion/useFieldEvents';
import { selectProductCorrelationIdMap } from '@core/reducers/eventingSlice';
import { selectInput } from '@core/reducers/inputsSlice';
import ErrorMessage from '@core/shared/components/ErrorMessage';
import cn from '@core/utils/classNames';

import styles from './PiiForm.module.css';
import type { PiiFormSectionTypes } from './type';
import { validateInputs } from './validateInputs';

const PiiFormSection: FC<PiiFormSectionTypes> = ({
  setEditMode,
  editMode,
  location,
  header = 'Your Information',
  piiLeadErrors = [],
  firstName,
  lastName,
  email,
  phone,
  setFirstName,
  setLastName,
  setEmail,
  setPhone,
  onSubmit,
  formErrors,
  setFormErrors,
  program,
}) => {
  // flags
  const flags = useFeatureFlags();

  // Field event tracking state
  const [canFireFieldInputted, setCanFireFieldInputted] = useState({
    firstName: true,
    lastName: true,
    email: true,
    phone: true,
  });

  const productCorrelationIdMap = useSelector(selectProductCorrelationIdMap);

  // Get startTime from store
  const startTime = useSelector((state) => selectInput(state, 'startTime'));

  // Field events setup
  const { fieldInputted: firstNameFieldInputted } = useFieldEvents({
    correlationId: productCorrelationIdMap[program?.id],
    formContext: {
      formName: 'voyager',
      formType: 'Lead Form',
    },
    userInputField: {
      autofilled: false,
      fieldName: 'first-name',
      fieldLabel: 'First Name',
      fieldType: 'text',
      fieldValue: 'SANITIZED',
      fieldNumber: 1,
    },
  });

  const { fieldInputted: lastNameFieldInputted } = useFieldEvents({
    correlationId: productCorrelationIdMap[program?.id],
    formContext: {
      formName: 'voyager',
      formType: 'Lead Form',
    },
    userInputField: {
      autofilled: false,
      fieldName: 'last-name',
      fieldLabel: 'Last Name',
      fieldType: 'text',
      fieldNumber: 2,
      fieldValue: 'SANITIZED',
    },
  });

  const { fieldInputted: emailFieldInputted } = useFieldEvents({
    correlationId: productCorrelationIdMap[program?.id],
    formContext: {
      formName: 'voyager',
      formType: 'Lead Form',
    },
    userInputField: {
      autofilled: false,
      fieldName: 'email',
      fieldLabel: 'Email',
      fieldType: 'text',
      fieldValue: 'SANITIZED',
      fieldNumber: 3,
    },
  });

  const { fieldInputted: phoneFieldInputted } = useFieldEvents({
    correlationId: productCorrelationIdMap[program?.id],
    formContext: {
      formName: 'voyager',
      formType: 'Lead Form',
    },
    userInputField: {
      autofilled: false,
      fieldName: 'phone',
      fieldLabel: 'Phone Number',
      fieldType: 'text',
      fieldValue: 'SANITIZED',
      fieldNumber: 4,
    },
  });

  const hasLeadError = (targetField: string) => piiLeadErrors.findIndex(({ field }) => field === targetField) !== -1;
  const leadPhoneError = hasLeadError('phone');

  const hasInputValue = (value: string | undefined | null): boolean =>
    typeof value === 'string' && value.trim().length > 0;

  // Field event handlers
  const handleFirstNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setFirstName(value);

    if (canFireFieldInputted.firstName && value.trim().length > 0 && !!flags.noPii && startTime === 0) {
      firstNameFieldInputted({
        userInputField: {
          fieldValue: 'SANITIZED',
        },
      });
      setCanFireFieldInputted((prev) => ({ ...prev, firstName: false }));
    }
  };

  const handleLastNameChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setLastName(value);

    if (canFireFieldInputted.lastName && value.trim().length > 0 && !!flags.noPii && startTime === 0) {
      lastNameFieldInputted({
        userInputField: {
          fieldValue: 'SANITIZED',
        },
      });
      setCanFireFieldInputted((prev) => ({ ...prev, lastName: false }));
    }
  };

  const handleEmailChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setEmail(value);

    if (canFireFieldInputted.email && value.trim().length > 0 && !!flags.noPii && startTime === 0) {
      emailFieldInputted({
        userInputField: {
          fieldValue: 'SANITIZED',
        },
      });
      setCanFireFieldInputted((prev) => ({ ...prev, email: false }));
    }
  };

  const handlePhoneChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setPhone(value);

    if (canFireFieldInputted.phone && value.trim().length > 0 && value !== '(' && !!flags.noPii && startTime === 0) {
      phoneFieldInputted({
        userInputField: {
          fieldValue: 'SANITIZED',
        },
      });
      setCanFireFieldInputted((prev) => ({ ...prev, phone: false }));
    }
  };

  // Focus handlers to reset field input tracking
  const handleFieldFocus = (fieldName: 'firstName' | 'lastName' | 'email' | 'phone') => {
    setCanFireFieldInputted((prev) => ({ ...prev, [fieldName]: true }));
  };

  const { elementClicked } = useElementEvents({
    webElement: {
      elementType: 'card',
      location: 'drawer',
      name: 'edit-info',
      htmlId: 'voyager-takeover-edit-pii',
    },
  });
  const handleSetEditMode = () => {
    elementClicked();
    setEditMode(true);
  };

  const handleEditConfirm = async () => {
    elementClicked();
    setFormErrors([]);
    const errors = await validateInputs(firstName, lastName, email, phone);
    if (errors.length) {
      setFormErrors(errors);
      return;
    }

    setEditMode(false);
  };
  return (
    <>
      <div className={styles.header}>
        <h5>{header}</h5>
        <button
          data-testid={`${location}-popup-edit-review-pii-button`}
          className={formErrors && styles.buttonDisabled}
          type="button"
          onClick={() => (editMode ? handleEditConfirm() : handleSetEditMode())}
        >
          {!editMode ? 'Edit' : 'Done'}
        </button>
      </div>
      {!editMode ? (
        <div className={styles.infoGrid}>
          <b>Name:</b>
          <p>{`${firstName} ${lastName}`}</p>

          <b>Email:</b>
          <p>{email}</p>

          <b>Phone:</b>
          <p>{phone}</p>
        </div>
      ) : (
        <>
          {formErrors?.length !== 0 && (
            <>
              <p className={styles.formErrorText}>
                We were unable to save your information. Please correct the errors listed below.
              </p>
              {formErrors?.map((obj) => (
                <ErrorMessage isFieldGroup={false} message={`Make sure the ${obj.field} field is valid.`} />
              ))}
            </>
          )}

          <form onSubmit={onSubmit}>
            <input
              className={cn(hasInputValue(firstName) ? styles.inputHasValue : undefined)}
              id="firstName"
              type="text"
              placeholder="First Name"
              data-testid="pii-form-first-name-input"
              defaultValue={firstName}
              onChange={handleFirstNameChange}
              onFocus={() => handleFieldFocus('firstName')}
            />

            <input
              className={cn(hasInputValue(lastName) ? styles.inputHasValue : undefined)}
              type="text"
              placeholder="Last Name"
              data-testid="pii-form-last-name-input"
              defaultValue={lastName}
              onChange={handleLastNameChange}
              onFocus={() => handleFieldFocus('lastName')}
            />

            <input
              className={cn(hasInputValue(email) ? styles.inputHasValue : undefined)}
              type="email"
              placeholder="Email"
              data-testid="pii-form-email-input"
              defaultValue={email}
              onChange={handleEmailChange}
              onFocus={() => handleFieldFocus('email')}
            />

            <InputMask
              className={cn(
                leadPhoneError ? styles.inputError : undefined,
                hasInputValue(phone) ? styles.inputHasValue : undefined
              )}
              mask="(999) 999-9999"
              maskChar=""
              autoComplete="on"
              type="text"
              placeholder="Phone"
              data-testid="pii-form-phone-input"
              defaultValue={phone}
              onChange={handlePhoneChange}
              onFocus={() => handleFieldFocus('phone')}
            />
            {leadPhoneError && flags?.phoneValidation && (
              <div className={styles.piiLeadError}>
                <ErrorMessage isFieldGroup={false} message="Please provide your valid phone number." />
              </div>
            )}
          </form>
        </>
      )}
    </>
  );
};

export { PiiFormSection };
